import React, { useState, useEffect, useContext } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  CheckIcon,
  DocumentTextIcon,
  TruckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ReactSelect from 'react-select';
import Swal from 'sweetalert2';
import api from '../../api';
import { AuthContext } from '../../components/AuthContext';

const GoodsReceipt = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // Form state
  const [form, setForm] = useState({
    purchaseIndent: null,
    transporterName: '',
    vehicleNumber: '',
    remarks: '',
    receivedItems: [],
    mandatoryItems: [],
    additionalItems: []
  });

  // Data states
  const [goodsReceipts, setGoodsReceipts] = useState([]);
  const [purchaseIndents, setPurchaseIndents] = useState([]);
  const [selectedPurchaseIndentDetails, setSelectedPurchaseIndentDetails] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    receiptDate: null
  });

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Modals
  const [viewModal, setViewModal] = useState({ open: false, receipt: null });

  // Edit state
  const [edit, setEdit] = useState(false);
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    fetchGoodsReceipts();
    fetchPurchaseIndents();
  }, [page, filters]);

  const fetchGoodsReceipts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/goods-receipts', {
        params: {
          page,
          limit: 10,
          ...filters,
          userId: user.user._id
        }
      });
      setGoodsReceipts(response.data.goodsReceipts || []);
      setTotalPages(response.data.totalPages || 1);
    } catch (error) {
      console.error('Error fetching goods receipts:', error);
      setGoodsReceipts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchaseIndents = async () => {
    try {
      const response = await api.get('/goods-receipts/purchase-indents', {
        params: {
          userId: user.user._id
        }
      });
      setPurchaseIndents(response.data || []);
    } catch (error) {
      console.error('Error fetching purchase indents:', error);
      setPurchaseIndents([]);
    }
  };

  const fetchPurchaseIndentDetails = async (purchaseIndentId) => {
    try {
      setLoading(true);
      const response = await api.get(`/goods-receipts/purchase-indent/${purchaseIndentId}`, {
        params: {
          userId: user.user._id
        }
      });
      
      setSelectedPurchaseIndentDetails(response.data);
      
      // Combine mandatory and additional items for receiving
      const mandatoryItems = response.data.mandatoryItemsToReceive?.map(item => ({
        ...item,
        itemCategory: 'mandatory',
        receivedQuantity: 0,
        receivedBoxes: 0,
        receivedPieces: 0
      })) || [];

      const additionalItems = response.data.additionalItemsToReceive?.map(item => ({
        ...item,
        itemCategory: 'additional',
        receivedQuantity: 0,
        receivedBoxes: 0,
        receivedPieces: 0
      })) || [];

      setForm(prev => ({
        ...prev,
        mandatoryItems,
        additionalItems,
        receivedItems: [...mandatoryItems, ...additionalItems] // Keep for backward compatibility
      }));
    } catch (error) {
      console.error('Error fetching purchase indent details:', error);
      setSelectedPurchaseIndentDetails(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      
      // Validate form
      if (!form.purchaseIndent) {
        Swal.fire('Error', 'Please select a purchase indent', 'error');
        return;
      }
      
      if (!form.transporterName.trim()) {
        Swal.fire('Error', 'Please enter transporter name', 'error');
        return;
      }
      
      if (!form.vehicleNumber.trim()) {
        Swal.fire('Error', 'Please enter vehicle number', 'error');
        return;
      }
      
      if (!form.receivedItems.some(item => item.receivedQuantity > 0)) {
        Swal.fire('Error', 'Please enter at least one received quantity', 'error');
        return;
      }
      
      const submitData = {
        purchaseIndent: form.purchaseIndent._id,
        transporterName: form.transporterName,
        vehicleNumber: form.vehicleNumber,
        remarks: form.remarks,
        receivedItems: form.receivedItems
          .filter(item => item.receivedQuantity > 0)
          .map(item => ({
            itemType: item.itemType,
            item: item.item,
            itemName: item.itemName,
            brandName: item.brandName,
            quantityType: item.quantityType,
            unit: item.unit,
            purchaseIndentItem: item.purchaseIndentItemId,
            orderedQuantity: item.orderedQuantity,
            receivedQuantity: item.receivedQuantity,
            orderedBoxes: item.orderedBoxes,
            receivedBoxes: item.receivedBoxes,
            orderedPieces: item.orderedPieces,
            receivedPieces: item.receivedPieces,
            proformaInvoiceAllocations: item.proformaInvoiceAllocations
          }))
      };

      if (edit) {
        await api.put(`/goods-receipts/${editId}`, submitData, {
          params: { userId: user.user._id }
        });
      } else {
        await api.post('/goods-receipts', submitData, {
          params: { userId: user.user._id }
        });
      }

      resetForm();
      setTabValue(0);
      fetchGoodsReceipts();
    } catch (error) {
      console.error('Error saving goods receipt:', error);
      Swal.fire('Error', 'Error saving goods receipt: ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      purchaseIndent: null,
      transporterName: '',
      vehicleNumber: '',
      remarks: '',
      receivedItems: [],
      mandatoryItems: [],
      additionalItems: []
    });
    setSelectedPurchaseIndentDetails(null);
    setEdit(false);
    setEditId(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'DRAFT':
        return 'bg-gray-100 text-gray-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'RECEIVED':
        return 'bg-green-100 text-green-800';
      case 'PARTIALLY_RECEIVED':
        return 'bg-blue-100 text-blue-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaginationPages = () => {
    const pages = [];
    const startPage = Math.max(1, page - 2);
    const endPage = Math.min(totalPages, page + 2);
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="w-full mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Goods Receipt Management</h1>
        <p className="text-gray-600">Create and manage goods receipts from purchase indents</p>
      </div>

      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setTabValue(0)}
            className={'py-2 px-1 border-b-2 font-medium text-sm border-blue-500 text-blue-600'}
          >
            Goods Receipts List
          </button>
          <button
            onClick={() => setTabValue(1)}
            className={'py-2 px-1 border-b-2 font-medium text-sm border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
          >
            <PlusIcon className="w-4 h-4 inline mr-1" />
            Create New Receipt
          </button>
        </nav>
      </div>

      {tabValue === 0 && (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Filters</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Status</option>
                  <option value="DRAFT">Draft</option>
                  <option value="PENDING">Pending</option>
                  <option value="RECEIVED">Received</option>
                  <option value="PARTIALLY_RECEIVED">Partially Received</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
              </div>
            </div>
          </div>

          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Goods Receipts
              </h3>
            </div>
            {loading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-gray-500">Loading goods receipts...</p>
              </div>
            ) : goodsReceipts.length === 0 ? (
              <div className="text-center py-8">
                <TruckIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No goods receipts</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Get started by creating a new goods receipt.
                </p>
                <div className="mt-6">
                  <button
                    onClick={() => setTabValue(1)}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <PlusIcon className="w-4 h-4 mr-2" />
                    New Goods Receipt
                  </button>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Receipt Number
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Purchase Indent
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Transport Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Items Count
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {goodsReceipts.map((receipt) => (
                      <tr key={receipt._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {receipt.receiptNumber}
                          </div>
                          <div className="text-sm text-gray-500">
                            {new Date(receipt.receiptDate).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {receipt.purchaseIndent?.indentNumber}
                          </div>
                          <div className="text-sm text-gray-500">
                            {receipt.purchaseIndent?.brand?.name}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(receipt.receiptDate).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {receipt.transporterName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {receipt.vehicleNumber}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {receipt.receivedItems?.length || 0} items
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          <button
                            onClick={() => setViewModal({ open: true, receipt })}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <EyeIcon className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {tabValue === 1 && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            Create New Goods Receipt
          </h2>
          
          <div className="grid grid-cols-1 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Purchase Indent *
              </label>
              <ReactSelect
                value={form.purchaseIndent}
                options={purchaseIndents}
                getOptionLabel={(option) => {
                  const completionStatus = option.isCompleted ? 'Completed' : 'In Progress';
                  const itemsToReceive = option.totalBalanceToReceive || 0;
                  return option.indentNumber + ' - ' + option.brand?.name + ' (' + itemsToReceive + ' items to receive) - ' + completionStatus;
                }}
                getOptionValue={(option) => option._id}
                onChange={(selectedOption) => {
                  setForm({ ...form, purchaseIndent: selectedOption });
                  if (selectedOption) {
                    fetchPurchaseIndentDetails(selectedOption._id);
                  } else {
                    setSelectedPurchaseIndentDetails(null);
                    setForm(prev => ({ ...prev, receivedItems: [] }));
                  }
                }}
                placeholder="Select purchase indent with items to receive..."
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>

            {form.purchaseIndent && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Transport Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Transporter Name *
                    </label>
                    <input
                      type="text"
                      value={form.transporterName}
                      onChange={(e) => setForm({ ...form, transporterName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter transporter name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Vehicle Number *
                    </label>
                    <input
                      type="text"
                      value={form.vehicleNumber}
                      onChange={(e) => setForm({ ...form, vehicleNumber: e.target.value.toUpperCase() })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter vehicle number"
                      required
                    />
                  </div>
                </div>
              </div>
            )}

            {form.purchaseIndent && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Remarks
                </label>
                <textarea
                  value={form.remarks}
                  onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter any remarks..."
                />
              </div>
            )}

            {/* Mandatory Items Section */}
            {form.mandatoryItems.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Mandatory Items to Receive ({form.mandatoryItems.length})
                  </h3>
                  <div className="text-sm text-gray-500">
                    Required items from purchase indent
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Item Name
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Required Qty
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Balance to Receive
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Received Qty *
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {form.mandatoryItems.map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {item.itemName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {item.unit} | {item.brandName}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.requiredQuantity} {item.unit}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {item.balanceToReceive} {item.unit}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <input
                              type="number"
                              min="0"
                              max={item.balanceToReceive}
                              value={item.receivedQuantity || 0}
                              onChange={(e) => {
                                const newQuantity = parseFloat(e.target.value) || 0;
                                const updatedItems = [...form.mandatoryItems];
                                updatedItems[index].receivedQuantity = newQuantity;
                                setForm({ 
                                  ...form, 
                                  mandatoryItems: updatedItems,
                                  receivedItems: [...updatedItems, ...form.additionalItems]
                                });
                              }}
                              className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                              placeholder="0"
                            />
                            <div className="text-xs text-gray-500 mt-1">
                              Max: {item.balanceToReceive} {item.unit}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Additional Items Section */}
            {form.additionalItems.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Additional Items to Receive ({form.additionalItems.length})
                  </h3>
                  <div className="text-sm text-gray-500">
                    Optional items from purchase indent
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Item Name
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Required Qty
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Balance to Receive
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Received Qty *
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {form.additionalItems.map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {item.itemName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {item.unit} | {item.brandName}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.requiredQuantity} {item.unit}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {item.balanceToReceive} {item.unit}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <input
                              type="number"
                              min="0"
                              max={item.balanceToReceive}
                              value={item.receivedQuantity || 0}
                              onChange={(e) => {
                                const newQuantity = parseFloat(e.target.value) || 0;
                                const updatedItems = [...form.additionalItems];
                                updatedItems[index].receivedQuantity = newQuantity;
                                setForm({ 
                                  ...form, 
                                  additionalItems: updatedItems,
                                  receivedItems: [...form.mandatoryItems, ...updatedItems]
                                });
                              }}
                              className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                              placeholder="0"
                            />
                            <div className="text-xs text-gray-500 mt-1">
                              Max: {item.balanceToReceive} {item.unit}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {form.purchaseIndent && form.mandatoryItems.length === 0 && form.additionalItems.length === 0 && selectedPurchaseIndentDetails && (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <TruckIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No items to receive</h3>
                <p className="mt-1 text-sm text-gray-500">
                  This purchase indent has no items with remaining balance to receive.
                </p>
              </div>
            )}

            {form.purchaseIndent && (form.mandatoryItems.length > 0 || form.additionalItems.length > 0) && (
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => {
                    resetForm();
                    setTabValue(0);
                  }}
                  className="bg-gray-600 text-white px-6 py-2 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Create Goods Receipt'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {viewModal.open && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Goods Receipt Details
              </h3>
              {viewModal.receipt && (
                <div className="grid grid-cols-1 gap-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Receipt Number</h4>
                      <p className="text-sm text-gray-900">{viewModal.receipt.receiptNumber}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Receipt Date</h4>
                      <p className="text-sm text-gray-900">{new Date(viewModal.receipt.receiptDate).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Purchase Indent</h4>
                      <p className="text-sm text-gray-900">{viewModal.receipt.purchaseIndent?.indentNumber}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Brand</h4>
                      <p className="text-sm text-gray-900">{viewModal.receipt.purchaseIndent?.brand?.name}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Transporter</h4>
                      <p className="text-sm text-gray-900">{viewModal.receipt.transporterName}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Vehicle Number</h4>
                      <p className="text-sm text-gray-900">{viewModal.receipt.vehicleNumber}</p>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Items Received</h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Item Name
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Ordered Qty
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Received Qty
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {viewModal.receipt.receivedItems?.map((item, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {item.itemName}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {item.orderedQuantity} {item.unit}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {item.receivedQuantity} {item.unit}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  
                  {viewModal.receipt.remarks && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Remarks</h4>
                      <p className="text-sm text-gray-900">{viewModal.receipt.remarks}</p>
                    </div>
                  )}
                </div>
              )}
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setViewModal({ open: false, receipt: null })}
                  className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoodsReceipt;
